import axios from 'axios'
import { Decimal } from 'decimal.js'
import { mapGetters } from 'vuex'
import constants from '~/lib/constants'

function isUsableBasketItem (item) {
  if (!item || typeof item !== 'object') {
    return false
  }
  // RedBean load() of a missing modifier_id serializes as id 0; skip those ghosts.
  // Client-side new rows have no id yet and must remain.
  if (item.id === 0 || item.id === '0') {
    return false
  }
  return true
}

function usableBasketItems (raw) {
  const items = Array.isArray(raw) ? raw : Object.values(raw || {})
  return items.filter(isUsableBasketItem)
}

function toFiniteNumber (value, fallback = 0) {
  if (value === null || value === undefined || value === '') {
    return fallback
  }
  const n = parseFloat(String(value).replace(',', '.'))
  return Number.isFinite(n) ? n : fallback
}

function basketLineAmount (item) {
  if (!isUsableBasketItem(item)) {
    return new Decimal(0)
  }
  const price = toFiniteNumber(item.price, 0)
  const count = toFiniteNumber(item.count, 0)
  const resourceCount = parseInt(item.resource_count, 10) > 1 ? parseInt(item.resource_count, 10) : 1
  return new Decimal(price).times(count).times(resourceCount)
}

export default {
  mixins: [constants],
  computed: {
    ...mapGetters(['allSettings', 'allCustomers', 'allBillingcustomers', 'baseDomain', 'shopId', 'hostName', 'locale', 'basketItems', 'linkedStores', 'basketIds', 'pos', 'activeBasket', 'floating', 'floatingReceipt', 'apiKey', 'activeOrder']),
    myBasketId () {
      return this.basketIds ? this.basketIds[this.shopId + '-' + this.data.index] : null
    },
    myBasketIds () {
      let basketIds = this.basketIds
      let ret = {}
      for (var shopIdWithIndex in basketIds) {
        if (Object.prototype.hasOwnProperty.call(basketIds, shopIdWithIndex)) {
          let siArr = shopIdWithIndex.split('-')
          let shopId = siArr[0]
          let index = siArr[1]
          if (index == this.activeBasket) {
            ret[shopId] = basketIds[shopIdWithIndex]
          }
        }
      }
      return ret
    },
    myBasketItems () {
      let basketItems = this.basketItems
      let ret = {}
      for (var shopIdWithIndex in basketItems) {
        if (Object.prototype.hasOwnProperty.call(basketItems, shopIdWithIndex)) {
          let siArr = shopIdWithIndex.split('-')
          let shopId = siArr[0]
          let index = siArr[1]
          if (index == this.activeBasket) {
            ret[shopId] = usableBasketItems(basketItems[shopIdWithIndex])
          }
        }
      }
      return ret
    },
    basketsWithItems () {
      let basketItems = this.basketItems
      let ret = {}
      for (var shopIdWithIndex in basketItems) {
        if (Object.prototype.hasOwnProperty.call(basketItems, shopIdWithIndex)) {
          let siArr = shopIdWithIndex.split('-')
          let shopId = siArr[0]
          let index = siArr[1]
          if (index == this.activeBasket && basketItems[shopIdWithIndex]) {
            const items = usableBasketItems(basketItems[shopIdWithIndex])
            if (items.length) {
              ret[shopId] = items
            }
          }
        }
      }
      return ret
    },
    masterOrderId () {
      return this.orderIds[this.shopId] || false
    },
    due () {
      let due = new Decimal(this.basketTotal)
      for (let i = 0; i < this.payments.length; i++) {
        due = due.minus(new Decimal(this.payments[i].amount))
      }
      if (due.toNumber() < 0) {
        return 0
      }
      return due.toDecimalPlaces(2).toNumber()
    },
    displayedTotal () {
      if (!this.virtualPayments || !this.virtualPayments.length) {
        return this.basketTotal
      }
      let total = new Decimal(this.basketTotal)
      for (let i = 0; i < this.virtualPayments.length; i++) {
        total = total.plus(new Decimal(this.virtualPayments[i].amount))
      }
      return total.toDecimalPlaces(2).toNumber()
    },
    displayedTotalVatFree () {
      return new Decimal(this.basketTotalVatFree).toDecimalPlaces(2).toNumber()
    },
    displayedTotalVat () {
      return this.displayedTotal - this.displayedTotalVatFree
    },
    vivaPayments () {
      let payments = []
      for (var i = 0; i < this.payments.length; i++) {
        if (this.payments[i].type === 'viva') {
          payments.push(this.payments[i])
        }
      }
      return payments
    },
    unresolvedVivaPayments () {
      let payments = []
      if (this.paymentterminal === 'external') {
        return payments
      }
      for (var i = 0; i < this.payments.length; i++) {
        if (this.payments[i].type === 'viva') {
          if (!this.payments[i].resolved) {
            payments.push(this.payments[i])
          }
        }
      }
      return payments
    },
    // A Viva payment that is already resolved means the terminal charge succeeded and
    // money has moved, even if postOrders/finalizeOrders later failed. Used to guard the
    // Viva-specific recovery logic so we never lose track of a charge that already happened.
    hasResolvedVivaPayment () {
      return this.vivaPayments.some(payment => payment.resolved)
    },
    mobilepayPayments () {
      let payments = []
      for (var i = 0; i < this.payments.length; i++) {
        if (this.payments[i].type === 'mobilepay') {
          payments.push(this.payments[i])
        }
      }
      return payments
    },
    unresolvedMobilepayPayments () {
      let payments = []
      if (!this.allSettings[this.shopId + '-' + this.locale] || !this.allSettings[this.shopId + '-' + this.locale].pay_option18_msn_pos) {
        return payments
      }
      for (var i = 0; i < this.payments.length; i++) {
        if (this.payments[i].type === 'mobilepay') {
          if (!this.payments[i].resolved) {
            payments.push(this.payments[i])
          }
        }
      }
      return payments
    },
    bamboraPayments () {
      let payments = []
      for (var i = 0; i < this.payments.length; i++) {
        if (this.payments[i].type === 'admincard' || this.payments[i].type === 'mobilepay' || this.payments[i].type === 'masterpass' || this.payments[i].type === 'pivo' || this.payments[i].type === 'siirto' || this.payments[i].type === 'virtualcard') {
          payments.push(this.payments[i])
        }
      }
      return payments
    },
    unresolvedBamboraPayments () {
      let payments = []
      for (var i = 0; i < this.payments.length; i++) {
        if (!this.payments[i].resolved) {
          if (this.paymentterminal === 'external' && this.payments[i].type === 'admincard') {
            continue
          }
          if (this.payments[i].type === 'admincard' || this.payments[i].type === 'mobilepay' || this.payments[i].type === 'masterpass' || this.payments[i].type === 'pivo' || this.payments[i].type === 'siirto' || this.payments[i].type === 'virtualcard') {
            payments.push(this.payments[i])
          }
        }
      }
      return payments
    },
    totalPayments () {
      let amount = new Decimal(0)
      for (let i = 0; i < this.payments.length; i++) {
        amount = amount.plus(new Decimal(this.payments[i].amount))
      }
      return amount.toDecimalPlaces(2).toNumber()
    },
    cardAmount () {
      let amount = new Decimal(0)
      for (let i = 0; i < this.payments.length; i++) {
        if (this.payments[i].type === 'admincard' || this.payments[i].type === 'viva') {
          amount = amount.plus(new Decimal(this.payments[i].amount))
        }
      }
      return amount.toDecimalPlaces(2).toNumber()
    },
    mobilePaymentAmount () {
      let amount = new Decimal(0)
      for (let i = 0; i < this.payments.length; i++) {
        if (this.payments[i].type === 'mobilepay' || this.payments[i].type === 'masterpass' || this.payments[i].type === 'pivo' || this.payments[i].type === 'siirto' || this.payments[i].type === 'virtualcard') {
          amount = amount.plus(new Decimal(this.payments[i].amount))
        }
      }
      return amount.toDecimalPlaces(2).toNumber()
    },
    mobilePaymentType () {
      for (var i = 0; i < this.payments.length; i++) {
        if (this.payments[i].type === 'mobilepay' || this.payments[i].type === 'masterpass' || this.payments[i].type === 'pivo' || this.payments[i].type === 'siirto') {
          return this.payments[i].type
        }
        if (this.payments[i].type === 'virtualcard') {
          return 'creditcards'
        }
      }
      return false
    },
    voucherAmount () {
      let amount = new Decimal(0)
      for (let i = 0; i < this.payments.length; i++) {
        if (this.payments[i].type === 'voucher') {
          amount = amount.plus(new Decimal(this.payments[i].amount))
        }
      }
      return amount.toDecimalPlaces(2).toNumber()
    },
    returnable () {
      let paid = new Decimal(0)
      for (let i = 0; i < this.payments.length; i++) {
        paid = paid.plus(new Decimal(this.payments[i].amount))
      }
      let returnable = paid.minus(new Decimal(this.basketTotal).toDecimalPlaces(2))
      if (returnable > 0) {
        return returnable.toDecimalPlaces(2).toNumber()
      }
      return 0
    },
    data () {
      return JSON.parse(this.component.data)
    },
    isConfirmedOrder () {
      if (this.activeOrder && parseInt(this.activeOrder.confirmed) === this.ORDER_CONFIRMED) {
        return true
      }
      return false
    },
    isOpenOrder () {
      if (this.activeOrder) {
        return true
      }
      for (var shopId in this.myBasketItems) {
        if (Object.prototype.hasOwnProperty.call(this.myBasketItems, shopId)) {
          let items = this.myBasketItems[shopId]
          for (var i = 0; i < items.length; i++) {
            if (parseInt(items[i].order_id)) {
              return true
            }
          }
        }
      }
      return false
    },
    hasVisiblePricing () {
      // For now we'll enable showing the pricing regardless of the product setting
      return true
      /*
      for (var shopId in this.myBasketItems) {
        if (Object.prototype.hasOwnProperty.call(this.myBasketItems, shopId)) {
          let items = this.myBasketItems[shopId]
          for (var i = 0; i < items.length; i++) {
            if (parseInt(items[i].hide_pricing) && parseInt(items[i].hide_pricing) !== 4) { // Open order (allow payment) == 4
              return false
            }
          }
        }
      }
      return true
      */
    },
    numBaskets () {
      let total = 0
      for (var shopId in this.myBasketItems) {
        if (Object.prototype.hasOwnProperty.call(this.myBasketItems, shopId)) {
          total++
        }
      }
      return total
    },
    numBasketItems () {
      let total = parseInt(0, 10)
      for (var shopId in this.myBasketItems) {
        if (Object.prototype.hasOwnProperty.call(this.myBasketItems, shopId)) {
          let items = this.myBasketItems[shopId]
          for (var i = 0; i < items.length; i++) {
            if (items[i].reservation_desc === null) {
              total += Math.abs(toFiniteNumber(items[i].count, 0))
            } else {
              total++
            }
          }
        }
      }
      return total
    },
    shouldShowMerchantDetails () {
      let total = 0
      for (var shopId in this.myBasketItems) {
        if (Object.prototype.hasOwnProperty.call(this.myBasketItems, shopId)) {
          let items = this.myBasketItems[shopId]
          if (items.length) {
            total++
            if (shopId != this.shopId || total > 1) {
              return true
            }
          }
        }
      }
      return false
    },
    hasExternalProducts () {
      for (var shopId in this.myBasketItems) {
        if (Object.prototype.hasOwnProperty.call(this.myBasketItems, shopId)) {
          let items = this.myBasketItems[shopId]
          if (items.length && shopId != this.shopId) {
            return true
          }
        }
      }
      return false
    },
    hasLockedItems () {
      for (var shopId in this.myBasketItems) {
        if (Object.prototype.hasOwnProperty.call(this.myBasketItems, shopId)) {
          let items = this.myBasketItems[shopId]
          for (var i = 0; i < items.length; i++) {
            if (items[i].locked) {
              return true
            }
          }
        }
      }
      return false
    },
    basketEmpty () {
      for (var shopId in this.myBasketItems) {
        if (Object.prototype.hasOwnProperty.call(this.myBasketItems, shopId)) {
          let items = this.myBasketItems[shopId]
          if (items && items.length) {
            return false
          }
        }
      }
      return true
    },
    basketHasFakeItems () {
      for (var shopId in this.myBasketItems) {
        if (Object.prototype.hasOwnProperty.call(this.myBasketItems, shopId)) {
          let items = this.myBasketItems[shopId]
          if (items && items.length) {
            if (items.find(i => !i.id)) {
              return true
            }
          }
        }
      }
      return false
    },
    basketTotals () {
      let totals = []
      for (var shopId in this.myBasketItems) {
        if (Object.prototype.hasOwnProperty.call(this.myBasketItems, shopId)) {
          let items = this.myBasketItems[shopId]
          totals[shopId] = 0
          for (var i = 0; i < items.length; i++) {
            totals[shopId] = new Decimal(totals[shopId]).add(basketLineAmount(items[i])).toNumber()
          }
        }
      }
      return totals
    },
    basketTotal () {
      let total = new Decimal(0)
      for (var shopID in this.myBasketItems) {
        if (Object.prototype.hasOwnProperty.call(this.myBasketItems, shopID)) {
          let items = this.myBasketItems[shopID]
          for (var i = 0; i < items.length; i++) {
            total = total.add(basketLineAmount(items[i]))
          }
        }
      }
      total = total.add(new Decimal(this.deliveryTotal))
      return total.toNumber()
    },
    basketTotalVatFree () {
      let total = new Decimal(0)
      for (var shopID in this.myBasketItems) {
        if (Object.prototype.hasOwnProperty.call(this.myBasketItems, shopID)) {
          let items = this.myBasketItems[shopID]
          for (var i = 0; i < items.length; i++) {
            if (items[i].valuecard_id) {
              continue;
            }
            if (items[i].vat === null || items[i].vat === 'null') {
              continue
            }
            const vat = toFiniteNumber(items[i].vat, 0)
            total = total.add(basketLineAmount(items[i]).dividedBy(1 + (vat / 100)))
          }
        }
      }
      total = total.add(new Decimal(this.deliveryTotalVatFree))
      return total
    },
    deliveryTotal () {
      let total = new Decimal(0)
      for (let shopId in this.basketsWithItems) {
        const deliveryItems = this.groupBy(this.basketsWithItems[shopId], 'delivery_id')
        for (let deliveryId in deliveryItems) {
          const delivery = deliveryItems[deliveryId][0] ? deliveryItems[deliveryId][0].delivery : null
          const deliverymethod = this.shopDeliveryMethods && this.shopDeliveryMethods[shopId] && delivery && delivery.deliverymethod_id ? this.shopDeliveryMethods[shopId].find(dm => parseInt(dm.id, 10) === parseInt(delivery.deliverymethod_id, 10)) : null
          let price
          if (delivery && delivery.manualprice !== null && delivery.manualprice !== undefined) {
            price = delivery.manualprice
          } else if (deliverymethod && parseFloat(deliverymethod.price)) {
            price = deliverymethod.price 
          }
          if (deliverymethod && parseFloat(deliverymethod.cutoffprice) && this.basketTotals[shopId] >= parseFloat(deliverymethod.cutoffprice)) {
            price = 0
          }
          if (price) {
            price = parseFloat(String(price).replace(',','.'))
            total = total.add(new Decimal(price))
          }
        }
      }
      return total.toNumber()
    },
    deliveryTotals () {
      let totals = []
      for (let shopId in this.basketsWithItems) {
        let total = new Decimal(0)
        const deliveryItems = this.groupBy(this.basketsWithItems[shopId], 'delivery_id')
        for (let deliveryId in deliveryItems) {
          const delivery = deliveryItems[deliveryId][0] ? deliveryItems[deliveryId][0].delivery : null
          const deliverymethod = this.shopDeliveryMethods && this.shopDeliveryMethods[shopId] && delivery && delivery.deliverymethod_id ? this.shopDeliveryMethods[shopId].find(dm => parseInt(dm.id, 10) === parseInt(delivery.deliverymethod_id, 10)) : null
          let price
          if (delivery && delivery.manualprice !== null && delivery.manualprice !== undefined) {
            price = delivery.manualprice
          } else if (deliverymethod && parseFloat(deliverymethod.price)) {
            price = deliverymethod.price 
          }
          if (deliverymethod && parseFloat(deliverymethod.cutoffprice) && this.basketTotals[shopId] >= parseFloat(deliverymethod.cutoffprice)) {
            price = 0
          }
          if (price) {
            price = parseFloat(String(price).replace(',','.'))
            total = total.add(new Decimal(price))
          }
        }
        totals[shopId] = total.toNumber()
      }
      return totals
    },
    deliveryTotalVatFree () {
      let total = new Decimal(0)
      for (let shopId in this.basketsWithItems) {
        const deliveryItems = this.groupBy(this.basketsWithItems[shopId], 'delivery_id')
        for (let deliveryId in deliveryItems) {
          const delivery = deliveryItems[deliveryId][0] ? deliveryItems[deliveryId][0].delivery : null
          const deliverymethod = this.shopDeliveryMethods && this.shopDeliveryMethods[shopId] && delivery && delivery.deliverymethod_id ? this.shopDeliveryMethods[shopId].find(dm => parseInt(dm.id, 10) === parseInt(delivery.deliverymethod_id, 10)) : null
          const totals = []
          let itemTotal = 0
          let vatFree = 0
          let price
          if (delivery && delivery.manualprice !== null && delivery.manualprice !== undefined) {
            price = delivery.manualprice
          } else if (deliverymethod && parseFloat(deliverymethod.price)) {
            price = deliverymethod.price 
          }
          if (deliverymethod && parseFloat(deliverymethod.cutoffprice) && this.basketTotals[shopId] >= parseFloat(deliverymethod.cutoffprice)) {
            price = 0
          }
          if (price) {
            for (let i = 0; i < deliveryItems[deliveryId].length; i++) {
              const item = deliveryItems[deliveryId][i]
              const vatKey = item && item.vat != null ? item.vat : 0
              if (!totals[vatKey]) {
                totals[vatKey] = 0
              }
              const amount = basketLineAmount(item).toNumber()
              totals[vatKey] += amount
              itemTotal += amount
            }
            for (let vat in totals) {
              if (!itemTotal) {
                continue
              }
              vatFree += price * (totals[vat] / itemTotal) / ((100 + parseFloat(vat)) / 100)
            }
            if (vatFree && Number.isFinite(vatFree)) {
              total = total.add(new Decimal(vatFree))
            }
          }
        }
      }
      return total.toNumber()
    },
    basketCurrency () {
      for (var shopID in this.myBasketItems) {
        if (Object.prototype.hasOwnProperty.call(this.myBasketItems, shopID)) {
          let items = this.myBasketItems[shopID]
          for (var i = 0; i < items.length; i++) {
            return items[i].currency // the first one will do
          }
        }
      }
      return 'EUR'
    },
    basketHasTickets () {
      for (var shopID in this.myBasketItems) {
        if (Object.prototype.hasOwnProperty.call(this.myBasketItems, shopID)) {
          let items = this.myBasketItems[shopID]
          for (var i = 0; i < items.length; i++) {
            if (parseInt(items[i].show_id) || parseInt(items[i].product_subtype, 10) === 5) {
              return true // Only one needs to match
            }
          }
        }
      }
      return false
    },
    basketHasValuecards () {
      for (var shopID in this.myBasketItems) {
        if (Object.prototype.hasOwnProperty.call(this.myBasketItems, shopID)) {
          let items = this.myBasketItems[shopID]
          for (var i = 0; i < items.length; i++) {
            if (parseInt(items[i].product_subtype, 10) === 6 || parseInt(items[i].product_subtype, 10) === 7) { // VALUECARD
              return true // Only one needs to match
            }
          }
        }
      }
      return false
    }
  },
  methods: {
    groupBy (arr, property) {
      return (arr || []).filter(isUsableBasketItem).reduce((acc, cur) => {
        acc[cur[property]] = [...acc[cur[property]] || [], cur]
        return acc
      }, {})
    },
    isUnresolved (payment) {
      if (this.paymentterminal !== 'external' && !payment.resolved && (payment.type === 'admincard' || payment.type === 'viva' || payment.type === 'mobilepay' || payment.type === 'masterpass' || payment.type === 'pivo' || payment.type === 'siirto' || payment === 'virtualcard')) {
        return true
      }
      return false
    },
    getPaymentTitle (type) {
      switch (type) {
        case 'admincash':
          return this._('Cash')
        case 'admincard':
          return this._('Card')
        case 'voucher':
          return this._('Voucher')
        case 'billing':
          return this._('Billing')
        case 'mobilepay':
          return this._('MobilePay')
        case 'viva':
          return this._('Viva')
        case 'masterpass':
          return this._('MasterPass')
        case 'pivo':
          return this._('Pivo')
        case 'siirto':
          return this._('Siirto')
        case 'virtualcard':
          return this._('Virtual card')
        case 'open':
          return this._('Open order')
        case 'rounding':
          return this._('Rounding difference')
      }
      return type
    },
    async initBasket (force, callback) {
      if (this.data && this.data.index) {
        this.index = this.data.index
      }
      if (this.floatingReceipt && !this.primary) { // Don't do anything if we have a floating receipt and this is not it
        return
      }
      const state = this.$store.state
      if (this.data && this.data.index) {
        this.index = this.data.index
        this.$store.dispatch('setActiveBasket', this.index)
        const basketId = state.basketIds[state.shopId + '-' + this.index]
        if ((force || !basketId) && state.baseDomain && state.shopId && this.apiKey) {
          let ax = axios.create()
          ax.defaults.withCredentials = true
          ax.defaults.headers.common['x-apikey'] = this.apiKey
          let basketArr = []
          let shopIdArr = []
          basketArr.push(ax.get(`${state.baseDomain}${state.shopId}/baskets.json?pos=${this.pos}&client=${this.clientid}&index=${this.index}`))
          shopIdArr.push(state.shopId)
          for (let i = 0; i < state.linkedStores.length; i++) {
            if (parseInt(state.linkedStores[i].active)) {
              basketArr.push(ax.get(`${state.baseDomain}${state.linkedStores[i].shopid}/baskets.json?shop=${state.shopId}&pos=${this.pos}&client=${this.clientid}&index=${this.index}`))
              shopIdArr.push(state.linkedStores[i].shopid)
            }
          }
          const responses = await Promise.allSettled(basketArr)
          const dispatchPromises = responses.map((response, i) => {
            if (response.status === 'fulfilled') {
              const shopId = shopIdArr[i]
              const basketId = response.value.data
              if (basketId) {
                return this.$store.dispatch('setBasketIds', {
                  shopId,
                  index: this.data.index,
                  basketId
                })
              }
            }
            return Promise.resolve()
          })
          await Promise.all(dispatchPromises)
        }
      }
      if (callback) {
        callback()
      }
    },
    initAdminBasket (orderIdentifier, callback) {
      if (this.floatingReceipt && !this.primary) { // Don't do anything if we have a floating receipt and this is not it
        return
      }
      let $this = this
      const state = this.$store.state
      let ax = axios.create()
      ax.defaults.withCredentials = true
      ax.defaults.headers.common['x-apikey'] = this.apiKey
      let basketArr = []
      let shopIdArr = []
      basketArr.push(ax.get(`${state.baseDomain}${state.shopId}/baskets.json?pos=${this.pos}&client=${this.clientid}&orderidentifier=${orderIdentifier}`))
      shopIdArr.push(state.shopId)
      axios.all(basketArr)
      .then(axios.spread(async function () {
        for (var i = 0; i < arguments.length; i++) {
          let basket = arguments[i]
          let shopId = shopIdArr[i]
          if (basket && basket.data) {
            await $this.$store.dispatch('setBasketIds', {
              shopId: shopId,
              index: 1,
              basketId: basket.data
            })
            await $this.$store.dispatch('reloadPayments', shopId + '-' + 1)
            // jQuery(document).trigger('page-updated') // eslint-disable-line
          }
        }
        if (callback) {
          callback()
        }
      }))
    },
    async closeAdminBasket (callback) {
      if (this.data && this.data.index) {
        this.index = this.data.index
      }
      if (this.floatingReceipt && !this.primary) { // Don't do anything if we have a floating receipt and this is not it
        return
      }
      const state = this.$store.state
      if (this.data && this.data.index) {
        this.index = this.data.index
        this.$store.dispatch('setActiveBasket', this.index)
        if (state.baseDomain && state.shopId && this.apiKey) {
          let ax = axios.create()
          ax.defaults.withCredentials = true
          ax.defaults.headers.common['x-apikey'] = this.apiKey
          let basketArr = []
          let shopIdArr = []
          basketArr.push(ax.get(`${state.baseDomain}${state.shopId}/baskets.json?pos=${this.pos}&client=${this.clientid}&index=${this.index}`))
          shopIdArr.push(state.shopId)
          const responses = await Promise.allSettled(basketArr)
          let i = 0
          for (let response of responses) {
            if (response.status === 'fulfilled') {
              const shopId = shopIdArr[i]
              const basketId = response.value.data
              if (basketId) {
                await this.$store.dispatch('setBasketIds', {
                  shopId,
                  index: this.data.index,
                  basketId
                })
                await this.$store.dispatch('reloadPayments', shopId + '-' + this.data.index)
                // jQuery(document).trigger('page-updated') // eslint-disable-line
              }
            }
            i++
          }
        }
      }
      if (callback) {
        callback()
      }
    },
    async reloadPayments (basketShopIdWithIndex) {
      if (this.floatingReceipt && !this.primary) { // Don't do anything if we have a floating receipt and this is not it
        return
      }
      await this.$store.dispatch('reloadPayments', basketShopIdWithIndex)
    }
  },
  watch: {
    pos: {
      handler (newValue) {
        // This watcher exists in case some asshole changes our client's POS while we're logged in
        if (newValue) {
          window.location.reload()
        }
      }
    }
  }
}